import AIChatPanel from '@/chat/AIChatPanel';
import { InlineEventCreator, InlineTriggerCreator, InlineTypeCreator } from '@/components/InlineCreatePopovers';
import LocationSelector from '@/components/LocationSelector';
import GuidedLocationPicker from '@/components/GuidedLocationPicker';
import MyGoogleMap from '@/components/MyGoogleMap';
import { buildZoneIndex, resolveZoneCoords } from '@/components/map/zoneCoords';
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CommanServices } from '@/services/commanServices';
import { AlertCircle, AlertTriangle, Brain, Check, Cpu, Dumbbell, HelpCircle, Loader2, Map as MapIcon, MapPin, Minus, Sparkles, Target, X } from 'lucide-react';
import Logo from '@/assets/Img/logo.png';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Plus, Library, Rocket } from 'lucide-react';
import MultiSelectDropdown from '@/components/MultiSelectDropdown';
import { toast } from 'sonner';
import ParticipatingEntities from '@/components/ParticipatingEntities';
import { Zap } from 'lucide-react';
import { captureReporterGeolocation } from '@/utils/reporterGeolocation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { readStoredUser } from '@/auth/saasSession';
import {
  CREATION_REPORT_TYPES,
  creationAccessForUser,
  resolveCreationReportType,
} from '../auth/incidentPermissions';

const PAST_EXERCISE_TIME_ERROR = 'Exercise start time cannot be in the past.';

const EXERCISE_CLASSIFICATIONS = ['Tabletop', 'Partial', 'Full-scale', 'Field', 'Joint'];

const isExerciseStartInPast = (startValue) => {
  if (!startValue) return false;
  const startDate = new Date(startValue);
  return !Number.isNaN(startDate.getTime()) && startDate < new Date();
};

const isSupportedReportType = (reportType) =>
  reportType === 'Real Incident' || reportType === 'Exercise';

const sanitizeReportType = (reportType) =>
  reportType === 'Exercise' ? 'Exercise' : 'Real Incident';

const hasPrefilledExerciseWindows = (data) =>
  Array.isArray(data?.planned_windows) &&
  data.planned_windows.some((w) => w?.start || w?.end);

const isUnknownLocation = (location) => {
  if (location === 'unknown') return true;
  return Array.isArray(location) && location.includes('unknown');
};

const hasValidLocation = (location) => {
  if (isUnknownLocation(location)) return true;
  return Array.isArray(location) ? location.length > 0 : !!location;
};

const normalizeLocationIds = (location) => {
  const ids = Array.isArray(location) ? location : (location ? [location] : []);
  return ids.filter((id) => id && id !== 'unknown');
};

const extractDepartmentId = (entry) => {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object') return entry.department_id || entry.id || entry._id || null;
  return null;
};

const getDepartmentIdsForLocation = (locationId, locations = [], departments = []) => {
  const ids = new Set();
  const location = locations.find((loc) => String(loc.id || loc._id) === String(locationId));

  (location?.departments || []).forEach((dept) => {
    const deptId = extractDepartmentId(dept);
    if (deptId) ids.add(String(deptId));
  });

  departments.forEach((dept) => {
    const deptId = String(dept.id || dept._id || '');
    if (!deptId) return;
    const associated = dept.associated_locations || [];
    if (associated.some((loc) => String(loc) === String(locationId))) {
      ids.add(deptId);
    }
  });

  return Array.from(ids);
};

const getImpliedDepartmentIds = (locationValue, locations = [], departments = []) => {
  const implied = new Set();
  normalizeLocationIds(locationValue).forEach((locId) => {
    getDepartmentIdsForLocation(locId, locations, departments).forEach((deptId) => implied.add(deptId));
  });
  return Array.from(implied);
};

const syncImpactedDepartments = (locationValue, manualDepartmentIds, locations = [], departments = []) => {
  const manual = new Set(
    (manualDepartmentIds instanceof Set ? Array.from(manualDepartmentIds) : manualDepartmentIds || [])
      .filter((id) => id && id !== 'undefined')
      .map(String)
  );
  getImpliedDepartmentIds(locationValue, locations, departments).forEach((deptId) => manual.add(deptId));
  return Array.from(manual);
};

const NewIncidentModal = ({
  isOpen,
  isMinimized = false,
  onMinimize,
  onRestore,
  onClose,
  onIncidentCreated,
  preFillData = null
}) => {
  const currentUser = readStoredUser();
  const creationAccess = creationAccessForUser(currentUser);
  const { canCreateIncident, canCreateDrill } = creationAccess;
  const canCreateIncidentOrDrill = canCreateIncident || canCreateDrill;
  const initialReportType = resolveCreationReportType(
    sanitizeReportType(preFillData?.reportType),
    creationAccess,
  ) || sanitizeReportType(preFillData?.reportType);

  // Form state - updated for Reporter-focused incident reporting
  const [formData, setFormData] = useState({
    type_id: preFillData?.type_id || '',
    event_id: preFillData?.event_id || '',
    trigger_id: preFillData?.trigger_id || '',
    severity: preFillData?.severity || 'auto', // Default to 'auto' to maintain Use Auto
    location: preFillData?.location || '', // Changed to single string for single-select LocationSelector
    description: preFillData?.description || '',
    reporter_name: (() => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        return user?.full_name || user?.name || '';
      } catch (e) {
        return '';
      }
    })(),
    reporter_contact: (() => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        return user?.email || user?.phone || '';
      } catch (e) {
        return '';
      }
    })(),
    attachments: [], // Add attachments support
    tags: [],
    reportType: initialReportType,
    planned_windows: initialReportType === 'Exercise' && hasPrefilledExerciseWindows(preFillData)
      ? preFillData.planned_windows
      : [{ start: '', end: '' }],
    impacted_departments: [],
    // Exercise (Disaster Simulation) fields
    exercise_classification: '',
    participating_entities: [],
    parent_incident_id: preFillData?.parent_incident_id || null // Track parent incident for linking
  });

  // Inline creation tracking
  const [inlineCreatedItems, setInlineCreatedItems] = useState({
    type: null,    // { id, name, severity }
    event: null,   // { name, severity }
    trigger: null  // { name, severityOverride }
  });

  const [isUserOverride, setIsUserOverride] = useState(false);

  const [incidentTypes, setIncidentTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [classificationBundle, setClassificationBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [autoResolvedSeverity, setAutoResolvedSeverity] = useState('');
  const [aiLoaderDescription, setAiLoaderDescription] = useState(false);
  const [locations, setLocations] = useState([]);
  const locationZoneIndex = useMemo(() => buildZoneIndex(locations), [locations]);
  const [departments, setDepartments] = useState([]);
  const [aiAnalysisResults, setAiAnalysisResults] = useState(null);
  const [aiAnalyzingImage, setAiAnalyzingImage] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [triggerSearch, setTriggerSearch] = useState('');
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [aiChatHistory, setAiChatHistory] = useState([]);
  const [aiAppliedSuggestion, setAiAppliedSuggestion] = useState(null);
  const [locationAssistantRequested, setLocationAssistantRequested] = useState(false);
  const [guidedSearch, setGuidedSearch] = useState({
    active: false,
    selectedParentId: null,
    breadcrumb: [],
  });
  const [gpsData, setGpsData] = useState(null);
  const gpsDataRef = useRef(null);
  const gpsRequestRef = useRef(null);
  const [showAiConfirm, setShowAiConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingAiText, setPendingAiText] = useState('');
  const isBatchUpdating = useRef(false);
  const scrollContainerRef = useRef(null);
  const savedScrollTop = useRef(0);
  const manualImpactedDepartmentsRef = useRef(new Set());
  const excludedImpactedDepartmentsRef = useRef(new Set());
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState('');
  const [launchingScenario, setLaunchingScenario] = useState(false);

  useEffect(() => {
    if (!isOpen || !canCreateIncidentOrDrill) return;
    gpsDataRef.current = null;
    gpsRequestRef.current = null;
    setGpsData(null);
    const request = captureReporterGeolocation()
      .then((nextGps) => {
        gpsDataRef.current = nextGps;
        setGpsData(nextGps);
        return nextGps;
      })
      .catch(() => null)
      .finally(() => {
        if (gpsRequestRef.current === request) gpsRequestRef.current = null;
      });
    gpsRequestRef.current = request;
    void request;
    if (canCreateDrill) (async () => {
      try {
        const res = await CommanServices.listScenarios('status=Active');
        const data = res?.status ? (await res.data) : (res?.data ?? res);
        const arr = Array.isArray(data) ? data : (data?.scenarios || []);
        setScenarios(arr.filter((s) => String(s.status || '').toLowerCase() === 'active'));
      } catch (e) { /* noop */ }
    })();
  }, [canCreateDrill, canCreateIncidentOrDrill, isOpen]);

  const handlePlanFromScenario = async (scenarioIdArg) => {
    if (!canCreateDrill) {
      toast.error('You do not have permission to request a drill.');
      return;
    }
    const id = (typeof scenarioIdArg === 'string' ? scenarioIdArg : null) || selectedScenarioId;
    if (!id) return;

    // Validate the exercise window (Start required, not in past; End after Start unless Open End)
    const win = (formData.planned_windows && formData.planned_windows[0]) || {};
    if (!win.start) {
      toast.error('Please provide a Start Time for the exercise');
      return;
    }
    if (isExerciseStartInPast(win.start)) {
      toast.error(PAST_EXERCISE_TIME_ERROR);
      return;
    }
    if (!win.end_open) {
      if (!win.end) {
        toast.error('Please provide an End Time or mark the end as Open');
        return;
      }
      if (new Date(win.end) <= new Date(win.start)) {
        toast.error('End time must be after start time.');
        return;
      }
    }

    setLaunchingScenario(true);
    try {
      const launchBody = {
        schedule: false,
        start: win.start,
        end: win.end_open ? null : (win.end || null),
        end_open: !!win.end_open,
      };
      const res = await CommanServices.launchScenario(id, launchBody);
      const d = res?.data ?? res;
      const incidentId = d?.incident_id || d?.id || d?.incident?.id;
      if (incidentId) {
        toast.success('Exercise started from scenario — opening request stage');
        onClose?.();
        navigate(`/incident-details/${incidentId}`);
      } else {
        toast.error('Could not start the exercise');
      }
    } catch (e) {
      toast.error('Failed to start the exercise');
    } finally {
      setLaunchingScenario(false);
    }
  };

  // Exercises can ONLY come from scenarios. If nothing matches, open the
  // scenario builder pre-filled with the planner's current selections.
  const buildScenarioFromSelection = () => {
    if (!canCreateDrill) {
      toast.error('You do not have permission to request a drill.');
      return;
    }
    const locArr = normalizeLocationIds(formData.location);
    navigate('/scenario-library', {
      state: {
        createPrefill: {
          type_id: formData.type_id || '',
          event_id: (formData.event_id && formData.event_id !== 'none') ? formData.event_id : '',
          trigger_id: (formData.trigger_id && formData.trigger_id !== 'none') ? formData.trigger_id : '',
          location: locArr,
          impacted_departments: (formData.impacted_departments || []).filter((d) => d && d !== 'undefined'),
          exercise_classification: formData.exercise_classification || '',
        },
      },
    });
    onClose?.();
  };

  // Smart Match: best Active scenario for the current exercise selections
  // (Incident Type + Event/Trigger + Location + Departments + Exercise Classification).
  const perfectMatchScenario = useMemo(() => {
    if (formData.reportType !== 'Exercise' || !formData.type_id) return null;
    const locIds = normalizeLocationIds(formData.location).map(String);
    const deptIds = (formData.impacted_departments || []).map(String);
    const cls = formData.exercise_classification;
    const eventId = (formData.event_id && formData.event_id !== 'none') ? String(formData.event_id) : '';
    const triggerId = (formData.trigger_id && formData.trigger_id !== 'none') ? String(formData.trigger_id) : '';

    const candidates = scenarios.filter((s) => {
      if (String(s.type_id || '') !== String(formData.type_id)) return false;
      if (s.event_id && eventId && String(s.event_id) !== eventId) return false;
      if (s.trigger_id && triggerId && String(s.trigger_id) !== triggerId) return false;
      const sLocs = (s.location || []).map(String);
      if (sLocs.length && locIds.length && !sLocs.some((l) => locIds.includes(l))) return false;
      return true;
    });

    const scored = candidates.map((s) => {
      let score = 1; // type matched
      const sLocs = (s.location || []).map(String);
      if (locIds.length && sLocs.some((l) => locIds.includes(l))) score += 3;
      const sDepts = (s.impacted_departments || []).map(String);
      if (deptIds.length && sDepts.some((d) => deptIds.includes(d))) score += 2;
      if (cls && s.exercise_classification === cls) score += 2;
      if (eventId && String(s.event_id) === eventId) score += 1;
      if (triggerId && String(s.trigger_id) === triggerId) score += 1;
      return { s, score };
    }).sort((a, b) => b.score - a.score);

    return scored[0]?.s || null;
  }, [scenarios, formData.reportType, formData.type_id, formData.event_id, formData.trigger_id, formData.location, formData.impacted_departments, formData.exercise_classification]);

  // Map departmentId -> relationship_type (primary/secondary) from the selected location(s)
  const departmentRelationshipMap = useMemo(() => {
    const map = {};
    const locIds = normalizeLocationIds(formData.location).map(String);
    locIds.forEach((locId) => {
      const loc = locations.find((l) => String(l.id || l._id) === locId);
      (loc?.departments || []).forEach((dept) => {
        const id = String(dept.department_id || dept.id || dept._id || '');
        const rel = dept.relationship_type;
        const relVal = (rel && rel.value) ? rel.value : rel;
        if (id && relVal) {
          // primary wins if a department is primary in any selected location
          if (map[id] !== 'primary') map[id] = relVal;
        }
      });
    });
    return map;
  }, [formData.location, locations]);

  const captureCurrentGps = useCallback(() => {
    if (gpsDataRef.current) return Promise.resolve(gpsDataRef.current);
    if (gpsRequestRef.current) return gpsRequestRef.current;

    const request = captureReporterGeolocation()
      .then((nextGps) => {
        gpsDataRef.current = nextGps;
        setGpsData(nextGps);
        return nextGps;
      })
      .catch(() => null)
      .finally(() => {
        if (gpsRequestRef.current === request) gpsRequestRef.current = null;
      });

    gpsRequestRef.current = request;
    return request;
  }, []);

  const buildInitialFormData = () => {
    const userStr = localStorage.getItem('user');
    let defaultReporterName = '';
    let defaultReporterContact = '';

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        defaultReporterName = user.full_name || user.name || '';
        defaultReporterContact = user.email || user.phone_number || '';
      } catch (e) {
        console.error('Error parsing user data for pre-fill:', e);
      }
    }

    const reportType = resolveCreationReportType(
      sanitizeReportType(preFillData?.reportType),
      creationAccess,
    ) || sanitizeReportType(preFillData?.reportType);

    return {
      type_id: preFillData?.type_id || '',
      event_id: preFillData?.event_id || '',
      trigger_id: preFillData?.trigger_id || '',
      severity: preFillData?.severity || 'auto',
      location: preFillData?.location || '',
      description: preFillData?.description || '',
      reporter_name: defaultReporterName,
      reporter_contact: defaultReporterContact,
      attachments: [],
      tags: [],
      reportType,
      planned_windows: reportType === 'Exercise' && hasPrefilledExerciseWindows(preFillData)
        ? preFillData.planned_windows
        : [{ start: '', end: '' }],
      impacted_departments: [],
      exercise_classification: '',
      participating_entities: [],
      parent_incident_id: preFillData?.parent_incident_id || null,
    };
  };

  const resetDraftState = () => {
    const initial = buildInitialFormData();
    setFormData(initial);
    setInlineCreatedItems({ type: null, event: null, trigger: null });
    setAutoResolvedSeverity('');
    setIsUserOverride(false);
    setError(null);
    setSuccess(null);
    setTypeSearch('');
    setEventSearch('');
    setTriggerSearch('');
    setAiAnalysisResults(null);
    setAiChatHistory([]);
    setAiAppliedSuggestion(null);
    setIsAiChatOpen(false);
    setLocationAssistantRequested(false);
    setGuidedSearch({ active: false, selectedParentId: null, breadcrumb: [] });
    setGpsData(null);
    manualImpactedDepartmentsRef.current = new Set();
    excludedImpactedDepartmentsRef.current = new Set();
  };

  // Sync preFillData if it changes while modal is open (Task 4.18)
  useEffect(() => {
    if (preFillData && isOpen) {
      setFormData(prev => ({
        ...prev,
        type_id: preFillData.type_id || prev.type_id,
        event_id: preFillData.event_id || prev.event_id,
        trigger_id: preFillData.trigger_id || prev.trigger_id,
        location: preFillData.location || prev.location,
        severity: preFillData.severity || prev.severity,
        reportType: preFillData.reportType
          ? (resolveCreationReportType(sanitizeReportType(preFillData.reportType), creationAccess) || prev.reportType)
          : prev.reportType,
        parent_incident_id: preFillData.parent_incident_id || prev.parent_incident_id,
        description: preFillData.description || prev.description,
      }));
    }
  }, [canCreateDrill, canCreateIncident, isOpen, preFillData]);

  useEffect(() => {
    if (!canCreateIncidentOrDrill) return;
    setFormData((prev) => {
      const permittedType = resolveCreationReportType(prev.reportType, creationAccess);
      if (!permittedType || permittedType === prev.reportType) return prev;
      return {
        ...prev,
        reportType: permittedType,
        location: Array.isArray(prev.location) ? (prev.location[0] || '') : prev.location,
      };
    });
  }, [canCreateDrill, canCreateIncident, canCreateIncidentOrDrill]);

  useEffect(() => {
    if (!isSupportedReportType(formData.reportType)) return;
    const synced = syncImpactedDepartments(
      formData.location,
      manualImpactedDepartmentsRef.current,
      locations,
      departments
    ).filter((id) => !excludedImpactedDepartmentsRef.current.has(String(id)));
    setFormData((prev) => {
      const current = (prev.impacted_departments || []).map(String).sort().join(',');
      const next = synced.map(String).sort().join(',');
      if (current === next) return prev;
      return { ...prev, impacted_departments: synced };
    });
  }, [formData.reportType, formData.location, locations, departments]);

  // Fetch incident suggestions for cascading logic (same as working NewIncident component)
  // Removed fetchIncidentSuggestions - using incident classifications data directly

  // Fetch incident classifications data on mount (same as incident classification module)
  useEffect(() => {
    if (isOpen && canCreateIncidentOrDrill) {
      // Force clear all cached data when modal opens to ensure fresh fetch
      setIncidentTypes([]);
      setEvents([]);
      setTriggers([]);
      setClassificationBundle(null);
      setAutoResolvedSeverity('');

      // Always fetch fresh data when modal opens
      fetchIncidentTypes();
      fetchTeams();
      fetchLocations();
      fetchDepartments();
      fetchAppUsers();
    }
  }, [canCreateIncidentOrDrill, isOpen]);

  // Fetch fresh classification bundle when incident type changes
  useEffect(() => {
    if (formData.type_id) {
      fetchEvents(formData.type_id);
    } else {
      setEvents([]);
      setTriggers([]);
      setClassificationBundle(null);
    }
  }, [formData.type_id]);

  const fetchDepartments = async () => {
    try {
      const response = await CommanServices.departments();
      if (response.status) {
        const data = await response.data;
        const normalized = (Array.isArray(data) ? data : []).map(d => ({
          ...d,
          id: d.id || d._id
        }));
        setDepartments(normalized);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const fetchAppUsers = async () => {
    try {
      const response = await CommanServices.getAppUsers();
      if (response.status) {
        const data = await response.data;
        setAppUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching app users:', error);
      setAppUsers([]);
    }
  };

  const fetchIncidentTypes = async () => {
    try {
      // Fetch incident types with aggressive cache-busting to ensure fresh data
      const timestamp = Date.now();
      const response = await CommanServices.incidentClassifications();
      if (response.status) {
        const data = await response.data;
        const normalized = (Array.isArray(data) ? data : []).map(t => ({
          ...t,
          id: t.id || t._id
        }));
        setIncidentTypes(normalized);
      }
    } catch (error) {
      console.error('Error fetching incident types:', error);
      setIncidentTypes([]);
    }
  };

  const fetchEvents = async (typeId) => {
    try {
      // Fetch fresh classification bundle for selected type with aggressive cache-bustin

      const response = await CommanServices.getincidentclassificationtypes(typeId);

      if (response.status) {
        const bundle = await response.data;
        console.log('Fetched fresh classification bundle for type:', typeId, bundle);

        const normalizedEvents = (bundle.events || []).map(e => ({
          ...e,
          id: e.id || e._id
        }));
        const normalizedTriggers = (bundle.triggers || []).map(t => ({
          ...t,
          id: t.id || t._id
        }));

        setEvents(normalizedEvents);
        setTriggers(normalizedTriggers);
        // Store the full bundle for severity calculations
        setClassificationBundle({
          ...bundle,
          events: normalizedEvents,
          triggers: normalizedTriggers
        });
      } else {
        console.error('Failed to fetch classification bundle:', response.message);
        setEvents([]);
        setTriggers([]);
        setClassificationBundle(null);
      }
    } catch (error) {
      console.error('Error fetching classification bundle:', error);
      setEvents([]);
      setTriggers([]);
      setClassificationBundle(null);
    }
  };

  // fetchTriggers removed - now fetched together with events in fetchEvents

  // Helper functions removed - now using incident classification data directly

  const fetchTeams = async () => {
    try {
      const response = await CommanServices.getTeamsData();
      if (response.status) {
        const data = await response.data;
        const normalized = (Array.isArray(data) ? data : []).map(t => ({
          ...t,
          id: t.id || t._id
        }));
        setTeams(normalized);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]); // Ensure teams is always an array
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setSuccess(null);

    const isExercise = formData.reportType === 'Exercise';

    if ((isExercise && !canCreateDrill) || (!isExercise && !canCreateIncident)) {
      setError(isExercise
        ? 'You do not have permission to request a drill.'
        : 'You do not have permission to create an incident.');
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);

    // Exercise validation
    if (isExercise) {
      if (!formData.type_id) {
        setError("Incident Type is required for an Exercise");
        setIsSubmitting(false);
        return;
      }
      if (!hasValidLocation(formData.location)) {
        setError("Please select an Incident Location for the Exercise");
        setIsSubmitting(false);
        return;
      }
      if (!formData.exercise_classification) {
        setError("Please select an Exercise Classification");
        setIsSubmitting(false);
        return;
      }
      if (!formData.planned_windows || formData.planned_windows.some(w => !w.start || (!w.end && !w.end_open))) {
        setError("Please provide a Start, and either an End date/time or mark the end as Open");
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Location capture starts when the form opens. Never block emergency
      // reporting while the operating system is still resolving a coordinate.
      const reporterGps = gpsDataRef.current || gpsData;

      // Determine resolved severity and its source using exact calculation
      const severityData = calculateAutoSeverity();
      const finalSeverity = formData.severity === 'auto' ? severityData.severity : formData.severity;
      const resolvedFrom = formData.severity === 'auto' ? severityData.from.toLowerCase() : 'user';

      const payload = {
        ...formData,
        event_id: formData.event_id === "none" ? null : formData.event_id || null,
        trigger_id: formData.trigger_id === "none" ? null : formData.trigger_id || null,
        severity: finalSeverity || 'Medium',
        location: Array.isArray(formData.location)
          ? formData.location.filter(Boolean)
          : (formData.location ? [formData.location] : []),
        resolved_severity: finalSeverity || 'Medium',
        resolved_from: resolvedFrom,
        tags: formData.tags || [],
        title: `${selectedIncidentType?.incident_type || 'AI-Detected Incident'} - Reported by ${formData.reporter_name || 'Anonymous'}`,
        reporter_name: formData.reporter_name || '',
        reporter_contact: formData.reporter_contact || '',
        assigned_teams: [],
        notify_now: false,
        is_planned_event: false,
        planned_windows: isExercise ? JSON.stringify(formData.planned_windows) : null,
        impacted_departments: isExercise
          ? Array.from(new Set((formData.participating_entities || [])
              .map((en) => en.department_unit_id)
              .filter((id) => id && id !== 'undefined')))
          // Real incidents: the location-synced list (minus manual removals)
          : (formData.impacted_departments || []).filter((id) => id && id !== 'undefined'),
        status: isExercise ? 'requested' : 'open',
        is_exercise: isExercise,
        exercise_classification: isExercise ? (formData.exercise_classification || '') : null,
        participating_entities: isExercise ? JSON.stringify(formData.participating_entities || []) : null,
        ai_chat_history: JSON.stringify(aiChatHistory),
        ai_suggestion: JSON.stringify(aiAppliedSuggestion),
        gps_data: reporterGps ? JSON.stringify(reporterGps) : null,
        reporter_gps_capture_status: reporterGps ? 'captured' : 'unavailable',
        is_unknown_location: isUnknownLocation(formData.location),
        evidence: isUnknownLocation(formData.location) ? (formData.description || '').split('[AI Assistant Analysis]')[0].trim() : null
      };

      delete payload.attachments; // Remove attachments from payload to handle separately

      // 🔹 Convert to FormData
      const formDataToSend = new FormData();

      for (const key in payload) {
        const value = payload[key];
        if (Array.isArray(value)) {
          // Append arrays correctly (for tags, location, etc.)
          value.forEach((v, i) => {
            const finalValue = (key === 'location' && typeof v === 'object' && v !== null)
              ? JSON.stringify(v)
              : v;
            formDataToSend.append(`${key}[${i}]`, finalValue);
          });
        } else if (value !== null && value !== undefined) {
          formDataToSend.append(key, value);
        }
      }
      // Append attachments if any
      formData.attachments.forEach((file, i) => {
        formDataToSend.append(`files`, file);
      });


      const response = await CommanServices.addincidents(formDataToSend);
      const newIncident = response?.data?.id ? response.data : response;
      const createdIncidentId = newIncident?.id || newIncident?.incident_id;

      if (response?.status !== false && createdIncidentId) {
        console.log('Incident created successfully:', newIncident);
        setSuccess(
          isExercise
            ? `Drill ${createdIncidentId} requested successfully!`
            : `Incident ${createdIncidentId} created successfully!`
        );

        // The create endpoint returns the Incident object directly, while some
        // older endpoints return { data, status, message }. Finish immediately
        // for either shape so a successful report never remains on Reporting….
        setIsSubmitting(false);
        resetDraftState();
        onIncidentCreated?.(newIncident);
        onClose?.();
        navigate(`/incident-details/${createdIncidentId}`);
        return;
      } else {
        console.error(response.message)
        setError(response.message || 'Failed to create incident');
      }
    } catch (error) {
      console.error('Error submitting incident:', error);
      setError('Network error occurred while creating incident');
    } finally {
      setIsSubmitting(false);
    }
  };


  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Build an exercise entity row for a department, auto-pulling its leader(s) as contacts
  const buildEntityForDepartment = (deptId) => {
    const dept = departments.find((d) => String(d.id) === String(deptId));
    const contacts = [];
    const pushUnique = (c) => {
      if (c && c.id && !contacts.some((x) => x.id === c.id)) contacts.push(c);
    };
    (dept?.members || []).forEach((m) => {
      if (m?.is_leader) pushUnique({ id: m.id || m.user_id || m.name, name: m.name });
    });
    (dept?.leaders || []).forEach((uid) => {
      const id = typeof uid === 'string' ? uid : uid?.id;
      if (!id) return;
      const u = appUsers.find((x) => x.id === id);
      pushUnique({ id, name: u ? (u.name || u.full_name || u.user_account || u.email) : id });
    });
    if (contacts.length === 0 && dept?.department_leader) {
      pushUnique({ id: dept.department_leader, name: dept.department_leader });
    }
    return {
      department_unit_id: deptId,
      type: 'internal',
      contact_persons: contacts,
      role: '',
      notes: '',
      recommended: true,
    };
  };

  // When a location is selected for an Exercise, auto-add entities for the
  // departments/units associated with that location (from the Location & Zone module).
  const handleExerciseLocationChange = (value) => {
    const syncedDepartments = syncImpactedDepartments(
      value,
      manualImpactedDepartmentsRef.current,
      locations,
      departments
    );
    setFormData((prev) => ({ ...prev, location: value, impacted_departments: syncedDepartments }));
  };

  const handleImpactedDepartmentsChange = (selected) => {
    const newList = (selected || []).filter((id) => id && id !== 'undefined').map(String);
    const prevList = (formData.impacted_departments || []).map(String);
    // Track explicit removals so auto-fill won't re-add them; clear exclusion when re-added
    prevList.forEach((id) => { if (!newList.includes(id)) excludedImpactedDepartmentsRef.current.add(id); });
    newList.forEach((id) => excludedImpactedDepartmentsRef.current.delete(id));
    manualImpactedDepartmentsRef.current = new Set(newList);
    const syncedDepartments = syncImpactedDepartments(
      formData.location,
      manualImpactedDepartmentsRef.current,
      locations,
      departments
    ).filter((id) => !excludedImpactedDepartmentsRef.current.has(String(id)));
    updateFormData('impacted_departments', syncedDepartments);
  };

  const sortBySearchRelevance = (items, searchTerm, getLabel) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return items;

    return [...items].sort((a, b) => {
      const aLabel = (getLabel(a) || '').toLowerCase();
      const bLabel = (getLabel(b) || '').toLowerCase();
      const aStartsWith = aLabel.startsWith(normalizedSearch);
      const bStartsWith = bLabel.startsWith(normalizedSearch);

      if (aStartsWith !== bStartsWith) return aStartsWith ? -1 : 1;

      const aIndex = aLabel.indexOf(normalizedSearch);
      const bIndex = bLabel.indexOf(normalizedSearch);
      if (aIndex !== bIndex) return aIndex - bIndex;

      return aLabel.localeCompare(bLabel);
    });
  };

  const filteredIncidentTypes = useMemo(() => {
    const normalizedSearch = typeSearch.trim().toLowerCase();
    const matched = incidentTypes.filter((type) =>
      (type.incident_type || '').toLowerCase().includes(normalizedSearch)
    );

    return sortBySearchRelevance(matched, normalizedSearch, (type) => type.incident_type);
  }, [incidentTypes, typeSearch]);

  const filteredEvents = useMemo(() => {
    const normalizedSearch = eventSearch.trim().toLowerCase();
    const matched = events.filter((event) =>
      (event.name || '').toLowerCase().includes(normalizedSearch)
    );

    return sortBySearchRelevance(matched, normalizedSearch, (event) => event.name);
  }, [events, eventSearch]);

  const filteredTriggers = useMemo(() => {
    let baseTriggers = [];

    if (formData.event_id === "none") {
      baseTriggers = triggers;
    } else if (formData.event_id) {
      baseTriggers = triggers.filter(trigger => trigger.event_id === formData.event_id);
    }

    const normalizedSearch = triggerSearch.trim().toLowerCase();
    const matched = baseTriggers.filter((trigger) =>
      (trigger.name || '').toLowerCase().includes(normalizedSearch)
    );

    return sortBySearchRelevance(matched, normalizedSearch, (trigger) => trigger.name);
  }, [triggers, formData.event_id, triggerSearch]);

  // Inline creation handlers
  const handleTypeCreated = async (newType) => {
    console.log('New type created:', newType);

    // Mark as inline created
    setInlineCreatedItems(prev => ({
      ...prev,
      type: {
        id: newType.id,
        name: newType.incident_type,
        severity: newType.alert_level || newType.incident_severity
      }
    }));

    // Auto-select the new type
    updateFormData('type_id', newType.id);
    updateFormData('severity', newType.alert_level || newType.incident_severity);
    setIsUserOverride(false);

    // Refresh incident types data
    await fetchIncidentTypes();
  };

  const handleEventCreated = async (newEvent, updatedClassification) => {
    console.log('New event created:', newEvent);

    // Mark as inline created
    setInlineCreatedItems(prev => ({
      ...prev,
      event: {
        name: newEvent.event,
        severity: newEvent.severity
      }
    }));

    // Find the new event ID from the updated classification
    const eventId = `${newEvent.event}`;
    updateFormData('event_id', eventId);

    // Refresh events data for current type
    await fetchEvents(formData.type_id);
  };

  const handleTriggerCreated = async (triggerName, severityOverride, updatedClassification) => {
    console.log('New trigger created:', triggerName);

    // Mark as inline created
    setInlineCreatedItems(prev => ({
      ...prev,
      trigger: {
        name: triggerName,
        severityOverride: severityOverride
      }
    }));

    // Find the new trigger ID from the updated classification
    const triggerId = `${triggerName}`;
    updateFormData('trigger_id', triggerId);

    // Refresh triggers data for current event
    await fetchEvents(formData.type_id);
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300 shadow-sm';
      case 'medium':
      case 'moderate':
        return 'bg-orange-100 text-orange-800 border-orange-300 shadow-sm';
      case 'low':
      case 'minor':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300 shadow-sm';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // SeverityChip component - matches incident classification module styling
  const SeverityChip = ({ severity, size = "sm", showLabel = true }) => {
    if (!severity) return null;

    return (
      <Badge className={`${getSeverityColor(severity)} ${size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-1 text-xs'} font-medium border`}>
        {showLabel ? severity : ''}
      </Badge>
    );
  };

  // Created while reporting chip
  const CreatedChip = ({ type }) => (
    <Badge className="bg-blue-100 text-blue-800 border-blue-300 px-2 py-1 text-xs font-medium ml-2">
      Created while reporting
    </Badge>
  );

  const selectedIncidentType = incidentTypes.find(type => type.id === formData.type_id);
  const selectedEvent = events.find(event => event.id === formData.event_id);
  const selectedTrigger = triggers.find(trigger => trigger.id === formData.trigger_id);

  // Severity calculation using exact stored values from classification bundle
  const calculateAutoSeverity = () => {
    if (!classificationBundle) return { severity: '', from: '' };

    const typeSeverity = classificationBundle.type.severity;

    // Priority order: Trigger > Event > Type (exact as stored)

    // 1. Check trigger severity (highest priority)
    if (formData.trigger_id && formData.trigger_id !== "none") {
      const selectedTrigger = triggers.find(t => t.id === formData.trigger_id && t.event_id === formData.event_id);
      if (selectedTrigger?.severity) {
        return { severity: selectedTrigger.severity, from: 'Trigger' };
      }
    }

    // 2. Check event severity (medium priority) 
    if (formData.event_id && formData.event_id !== "none") {
      const selectedEvent = events.find(e => e.id === formData.event_id);
      if (selectedEvent?.severity) {
        return { severity: selectedEvent.severity, from: 'Event' };
      }
    }

    // 3. Fall back to type severity (lowest priority)
    return { severity: typeSeverity, from: 'Type' };
  };

  const [aiError, setAiError] = useState(null);

  const getSelectedLocationLabels = () => {
    const locationIds = Array.isArray(formData.location)
      ? formData.location
      : (formData.location ? [formData.location] : []);

    return locationIds
      .map((id) => {
        const loc = locations.find((item) => item.id == id);
        return loc?.auto_label || loc?.label || '';
      })
      .filter(Boolean);
  };

  const generateAiDescription = async () => {
    try {
      setAiLoaderDescription(true);
      setAiError(null);

      // Validation: Type and Location are required
      const typeId = formData.type_id;
      const locationId = formData.location;
      const hasLocation = Array.isArray(locationId) ? locationId.length > 0 : !!locationId;

      if (!typeId || !hasLocation) {
        setAiError("Please select both an Incident Type and a Location to generate a description.");
        setAiLoaderDescription(false);
        return;
      }

      const locationLabels = getSelectedLocationLabels();
      const locationLabel = locationLabels.join('; ');
      const typeName = incidentTypes.find((item) => item.id == formData.type_id)?.incident_type || '';

      const payload = {
        incident_type: typeName,
        event: events.find((item) => item.id == formData.event_id)?.name || '',
        trigger: triggers.find((item) => item.id == formData.trigger_id)?.name || '',
        location: locationLabel,
        severity: formData?.severity || '',
        notes: formData.description,
        attachments_count: formData.attachments?.length || 0
      };

      const res = await CommanServices.getAiDescription(payload);

      if (res?.status && res?.data?.description) {
        const aiText = res.data.description;

        if (formData.description && formData.description.trim() !== '') {
          setPendingAiText(aiText);
          setShowAiConfirm(true);
        } else {
          updateFormData('description', aiText);
        }
      } else {
        setAiError(res?.message || "Failed to generate AI description. Please try again.");
      }
    } catch (error) {
      console.error('AI Generation Error:', error);
      setAiError("An error occurred during AI generation.");
    } finally {
      setAiLoaderDescription(false);
    }
  }

  const fetchLocations = async () => {
    try {
      const response = await CommanServices.getlocationszonesdata();
      if (response.status) {
        const data = await response.data;
        const normalized = (Array.isArray(data) ? data : []).map(l => ({
          ...l,
          id: l.id || l._id
        }));
        setLocations(normalized);
        console.log('✅ LocationSelector: Fetched', normalized.length, 'locations with caching');
      }

    } catch (err) {
      console.error('❌ LocationSelector: Error fetching locations:', err);
    } finally {
    }
  };

  // Analyze the first attached image using backend AI endpoint.
  const analyzeImageWithAI = async () => {
    return
  };

  // Apply AI-detected names to the form (try to match existing type/event/trigger by name)
  const applyAiSuggestions = async (suggestion) => {
    isBatchUpdating.current = true;
    const results = suggestion || aiAnalysisResults;
    if (!results) return;

    const {
      incident_type, event, trigger, type_id, event_id, trigger_id,
      human_summary, is_new_proposal, severity,
      location_id, location_match
    } = results;

    let finalTypeId = type_id;
    let finalEventId = event_id;
    let finalTriggerId = trigger_id;

    // Point 8: Handle New Classification Proposal
    if (is_new_proposal) {
      try {
        const createRes = await CommanServices.aiCreateClassification({
          incident_type,
          event,
          trigger,
          severity: severity || 'MEDIUM',
          description: `AI Proposed: ${human_summary || 'Auto-created from reporter description'}`,
          rationale: results.rationale,
          user_id: formData.reporter_name || 'Anonymous Reporter'
        });

        if (createRes && createRes.id) {
          finalTypeId = createRes.id;
          finalEventId = event; // Use the proposed names as IDs for these fields
          finalTriggerId = trigger;

          // Refresh classifications to include the new one
          if (fetchIncidentTypes) fetchIncidentTypes();
        }
      } catch (err) {
        console.error("Failed to auto-create classification:", err);
      }
    }

    // Use functional update to ensure all fields are set in one batch
    setFormData(prev => {
      const next = { ...prev };

      // 1. Handle Type
      if (finalTypeId) {
        next.type_id = finalTypeId;
      } else if (incident_type) {
        const foundType = classificationBundle.find(t => t.incident_type.toLowerCase() === incident_type.toLowerCase());
        if (foundType) next.type_id = foundType.id;
      }

      // 2. Handle Event
      if (finalEventId) {
        next.event_id = finalEventId;
      } else if (event) {
        const foundEvent = events.find(e => (e.name || '').toLowerCase() === event.toLowerCase());
        if (foundEvent) next.event_id = foundEvent.id;
      }

      // 3. Handle Trigger
      if (finalTriggerId) {
        next.trigger_id = finalTriggerId;
      } else if (trigger) {
        // More flexible matching for triggers which might contain quotes or complex strings
        const normalize = (s) => (s || '').toLowerCase().replace(/['"]/g, '').trim();
        const foundTrigger = triggers.find(t => normalize(t.name) === normalize(trigger));
        if (foundTrigger) next.trigger_id = foundTrigger.id;
      }

      // 4. Handle Location (Epic 2: match from L & Z)
      if (location_id) {
        next.location = location_id;
      }

      // Handle Severity (Point 6 & 7)
      if (severity) {
        next.severity = severity.toLowerCase();
        setIsUserOverride(true); // Manually set by AI/User confirmation
      }

      // Rules 37–39: Normalize AI-generated description before appending
      // Trim, collapse blank lines, strip conversational filler openers
      const normalizeAiDescription = (text) => {
        if (!text) return '';
        return text
          .replace(/\r\n/g, '\n')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .replace(/^(Based on (the information|our conversation|what you'?ve (said|described)|your description)[^.]*\.\s*)/i, '')
          .replace(/^(It appears that |It seems that |It looks like )/i, '')
          .trim();
      };

      const rawSummary = human_summary
        ? human_summary
        : `This incident has been classified as "${incident_type || 'Unclassified'}" based on the observed "${event || 'Unknown'}" condition.`;
      const cleanSummary = normalizeAiDescription(rawSummary);
      const aiSummary = `[AI Assistant Analysis]\n${cleanSummary}`;

      // Prevent double-adding the summary if already present
      if (!prev.description.includes('[AI Assistant Analysis]')) {
        next.description = `${prev.description}${aiSummary}`;
      }

      return next;
    });

    // Reset batch update flag after a delay to allow useEffects to skip their reset logic
    setTimeout(() => {
      isBatchUpdating.current = false;
    }, 500);
  };

  // Unified size limits (video: 20MB, others: 20MB)
  const MAX_VIDEO_SIZE = 20 * 1024 * 1024;
  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      let files = [];
      let rejectedNames = [];

      Array.from(e.target.files).forEach((file) => {
        const isVideo = file.type && file.type.startsWith("video/");
        const limit = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
        const label = "20 MB";
        if (file.size <= limit) {
          files.push(file);
        } else {
          rejectedNames.push(`${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB > ${label})`);
        }
      });

      if (rejectedNames.length > 0) {
        alert(`The following files exceed the allowed size limit and were not added:\n\n${rejectedNames.join('\n')}`);
      }

      if (files.length > 0) {
        setFormData({
          ...formData,
          attachments: formData.attachments.concat(files),
        });
      }

      e.target.value = ""; // Reset input
    }
  };

  // Reset trigger selection when event changes (Smart Logic requirement)
  useEffect(() => {
    if (formData.event_id && !isBatchUpdating.current) {
      // Clear trigger when event changes to prevent invalid combinations
      updateFormData('trigger_id', '');
    }
  }, [formData.event_id]);

  // Real-time severity auto-resolution using stored classification data
  useEffect(() => {
    const severityData = calculateAutoSeverity();
    setAutoResolvedSeverity(severityData.severity);
    updateFormData('severity', severityData?.severity?.toLowerCase())
    setIsUserOverride(false);

    // Only update form severity if using auto mode (don't override user selections)
    if (formData.severity === 'auto' || formData.severity === '') {
      // This is just for display purposes - the calculated value is shown in the badge
      // The actual severity used will be determined in handleSubmit
    }
  }, [formData.type_id, formData.event_id, formData.trigger_id, classificationBundle, events, triggers]);

  const hasIncidentType = !!formData.type_id;
  const hasLocation = hasValidLocation(formData.location);
  const unknownLocationSelected = isUnknownLocation(formData.location);

  const isFormDirty = useMemo(() => {
    const description = (formData.description || '').trim();
    const location = formData.location;
    const hasLocation = Array.isArray(location) ? location.length > 0 : !!location;
    const hasClassification = !!(
      formData.type_id ||
      (formData.event_id && formData.event_id !== 'none') ||
      (formData.trigger_id && formData.trigger_id !== 'none')
    );
    const hasAttachments = (formData.attachments?.length || 0) > 0;
    const hasTags = (formData.tags?.length || 0) > 0;
    const hasImpactedDepartments = (formData.impacted_departments?.length || 0) > 0;
    const hasExerciseWindow = formData.reportType === 'Exercise'
      && (formData.planned_windows || []).some((w) => w.start || w.end);
    const hasAiUserInput = aiChatHistory.some((m) => m.role === 'user');
    const hasAiContent = !!(aiAppliedSuggestion || aiAnalysisResults);
    const hasInlineCreated = !!(inlineCreatedItems.type || inlineCreatedItems.event || inlineCreatedItems.trigger);

    return !!(
      description ||
      hasLocation ||
      hasClassification ||
      hasAttachments ||
      hasTags ||
      hasImpactedDepartments ||
      hasExerciseWindow ||
      hasAiUserInput ||
      hasAiContent ||
      hasInlineCreated ||
      isUserOverride
    );
  }, [formData, aiChatHistory, aiAppliedSuggestion, aiAnalysisResults, inlineCreatedItems, isUserOverride]);

  const handleCloseRequest = () => {
    if (isFormDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const handleDiscardReport = () => {
    setShowDiscardConfirm(false);
    resetDraftState();
    onClose();
  };

  const handleMinimizeClick = () => {
    if (scrollContainerRef.current) {
      savedScrollTop.current = scrollContainerRef.current.scrollTop;
    }
    onMinimize?.();
  };

  useEffect(() => {
    if (isOpen && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollTop.current;
    }
  }, [isOpen]);

  if (!isOpen && !isMinimized) return null;

  if (!canCreateIncidentOrDrill) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="alert">
        <div className="w-full max-w-md rounded-xl bg-white p-6 text-center shadow-2xl">
          <AlertCircle className="mx-auto h-9 w-9 text-amber-500" />
          <h3 className="mt-3 text-lg font-bold text-slate-900">Creation access unavailable</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Your current institution role cannot create incidents or request drills.
          </p>
          <Button className="mt-5" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <button
        type="button"
        onClick={onRestore}
        className="fixed bottom-20 right-6 z-[60] group"
        aria-label="Restore incident report draft"
      >
        <div className="relative">
          <div className="h-14 w-14 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
            <img src={Logo} alt="Restore draft" className="h-9 w-9 object-contain" />
          </div>
          {isFormDirty && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              Draft
            </span>
          )}
        </div>
      </button>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Incident Created Successfully!</h3>
          <p className="text-gray-600">{success}</p>
        </div>
      </div>
    );
  }

  // Involved Organizations and Departments — auto-filled from the linked
  // departments of the selected location. Shared by the Drill (Exercise)
  // and Real Incident forms.
  const involvedDepartmentsSection = (
              <div>
                <label className="block text-sm font-medium mb-2">Involved Organizations and Departments</label>
                <MultiSelectDropdown
                  options={departments.map(d => ({ value: d.id, label: d.department_name || d.name }))}
                  selectedValues={formData.impacted_departments}
                  onChange={handleImpactedDepartmentsChange}
                  placeholder={departments.length === 0 ? "No departments available (Create in Departments section)" : "Select departments involved..."}
                  isSearchable={true}
                  hideSelectedChips={true}
                />
                <p className="text-[11px] text-slate-400 mt-2">Auto-filled from the selected location — adjust to refine the scenario match.</p>
                {(formData.impacted_departments || []).length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2.5">
                    <style>{`
                      @keyframes smartSheen {
                        0% { transform: translateX(-130%); }
                        55%, 100% { transform: translateX(130%); }
                      }
                      .smart-rec-chip {
                        position: relative;
                        overflow: hidden;
                        background-image: linear-gradient(110deg, #6366f1, #8b5cf6, #a855f7);
                      }
                      .smart-rec-chip::after {
                        content: '';
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(75deg, transparent 42%, rgba(255,255,255,0.30) 50%, transparent 58%);
                        transform: translateX(-130%);
                        animation: smartSheen 6s ease-in-out infinite;
                        pointer-events: none;
                      }
                    `}</style>
                    <span className="smart-rec-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-sm">
                      <Sparkles className="h-3 w-3" /> Smartly recommended
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500">:</span>
                  </div>
                )}
                {(formData.impacted_departments || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="dept-role-list">
                    {[...(formData.impacted_departments || [])]
                      .sort((a, b) => {
                        const rank = (id) => {
                          const r = departmentRelationshipMap[String(id)];
                          return r === 'primary' ? 0 : (r === 'secondary' ? 1 : 2);
                        };
                        return rank(a) - rank(b);
                      })
                      .map((deptId) => {
                      const dept = departments.find((d) => String(d.id) === String(deptId));
                      const name = dept?.department_name || dept?.name || 'Department';
                      const role = departmentRelationshipMap[String(deptId)];
                      const isPrimary = role === 'primary';
                      return (
                        <span
                          key={deptId}
                          data-testid={`dept-role-${deptId}`}
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700"
                        >
                          {name}
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${
                            isPrimary
                              ? 'bg-emerald-100 text-emerald-700'
                              : (role === 'secondary' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500')
                          }`}>
                            {role || 'manual'}
                          </span>
                          <button
                            type="button"
                            data-testid={`dept-role-remove-${deptId}`}
                            onClick={() => handleImpactedDepartmentsChange((formData.impacted_departments || []).filter((id) => String(id) !== String(deptId)))}
                            className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors"
                            aria-label={`Remove ${name}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`nim-shell relative flex flex-col md:flex-row overflow-hidden transition-all duration-500 ease-in-out ${isAiChatOpen ? 'max-w-7xl' : 'max-w-4xl'} w-full max-h-[92vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top edge gradient strip — red for real incidents, indigo for drills */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[3px] z-10"
          style={{ background: formData.reportType === 'Exercise'
            ? 'linear-gradient(90deg, transparent, #7c3aed 30%, #4338ca 70%, transparent)'
            : 'linear-gradient(90deg, transparent, #ef4444 30%, #b91c1c 70%, transparent)' }}
        />
        <div ref={scrollContainerRef} className="flex-1 p-6 overflow-y-auto custom-scrollbar">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div
                className="flex h-[46px] w-[46px] items-center justify-center rounded-[14px] text-white"
                style={formData.reportType === 'Exercise'
                  ? { background: 'linear-gradient(135deg,#4338ca,#7c3aed)', boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 8px 24px rgba(67,56,202,0.35)' }
                  : { background: 'linear-gradient(135deg,#dc2626,#b91c1c)', boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 8px 24px rgba(220,38,38,0.35)' }}
              >
                {formData.reportType === 'Exercise'
                  ? <Target className="h-[21px] w-[21px]" />
                  : <AlertTriangle className="h-[21px] w-[21px]" />}
              </div>
              <h3 className="text-[20px] font-bold tracking-tight" style={{ color: '#0f172a' }}>{formData.reportType === 'Exercise' ? 'Request a Drill' : 'Report an Incident'}</h3>
            </div>

            <div className="flex items-center space-x-3">
              {formData.reportType !== 'Exercise' && (
              <Button
                type="button"
                variant={isAiChatOpen ? "secondary" : "outline"}
                size="sm"
                onClick={() => setIsAiChatOpen(!isAiChatOpen)}
                className="flex items-center space-x-2 rounded-full px-4 transition-all duration-300"
                style={{ background: 'linear-gradient(180deg,#ede9fe,#f3e8ff)', border: '1px solid #ddd6fe', color: '#6d28d9' }}
              >
                <div className={`relative ${isAiChatOpen ? 'animate-bounce' : ''}`}>
                  <Sparkles className={`h-4 w-4 ${isAiChatOpen ? 'fill-violet-600 text-violet-600' : 'text-slate-500'}`} />
                  {!isAiChatOpen && <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500"></span>
                  </span>}
                </div>
                <span className="font-medium">AI Assistant</span>
              </Button>
              )}

              {onMinimize && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMinimizeClick}
                  className="rounded-full hover:bg-slate-100 h-9 w-9 text-slate-500"
                  aria-label="Minimize incident report"
                >
                  <Minus className="h-5 w-5" />
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseRequest}
                className="rounded-full hover:bg-slate-100 h-9 w-9 text-slate-500"
                aria-label="Close incident report"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 animate-in fade-in slide-in-from-top-2">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Report Type Toggle — hidden when the caller pins a report type via preFillData */}
            {!isSupportedReportType(preFillData?.reportType) && (
              <div className="flex w-fit items-center gap-1" style={{ background: '#f1f3fa', border: '1px solid #e6e9f4', borderRadius: 999, padding: 5 }}>
                {canCreateIncident && (
                  <button
                    type="button"
                    data-testid="create-real-incident-type"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      reportType: CREATION_REPORT_TYPES.INCIDENT,
                      location: Array.isArray(prev.location) ? (prev.location[0] || '') : prev.location
                    }))}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-[13.5px] font-bold rounded-full transition-all"
                    style={formData.reportType === CREATION_REPORT_TYPES.INCIDENT
                      ? { background: 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', boxShadow: '0 4px 14px rgba(220,38,38,0.3)' }
                      : { background: 'transparent', color: '#64748b' }}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" /> Real Incident
                  </button>
                )}
                {canCreateDrill && (
                  <button
                    type="button"
                    data-testid="create-drill-type"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      reportType: CREATION_REPORT_TYPES.DRILL,
                      location: Array.isArray(prev.location) ? (prev.location[0] || '') : prev.location
                    }))}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-[13.5px] font-bold rounded-full transition-all"
                    style={formData.reportType === CREATION_REPORT_TYPES.DRILL
                      ? { background: 'linear-gradient(135deg,#4338ca,#7c3aed)', color: '#fff', boxShadow: '0 4px 14px rgba(67,56,202,0.3)' }
                      : { background: 'transparent', color: '#64748b' }}
                  >
                    <Target className="h-3.5 w-3.5" /> Request a Drill
                  </button>
                )}
              </div>
            )}

            {/* Exercise Window: Start / End / Open End — surfaced above the scenario match */}
            {formData.reportType === 'Exercise' && (
              <div className="p-4 space-y-3" style={{ background: 'linear-gradient(180deg,#f5f6ff,#f0f2fd)', border: '1px solid #dfe2fb', borderRadius: 16 }} data-testid="exercise-window">
                <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Exercise Window
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Start Time <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="datetime-local"
                      data-testid="exercise-start-time"
                      value={formData.planned_windows?.[0]?.start || ''}
                      onChange={(e) => {
                        const w = [...(formData.planned_windows || [{ start: '', end: '' }])];
                        w[0] = { ...(w[0] || {}), start: e.target.value };
                        updateFormData('planned_windows', w);
                      }}
                      className="h-9 text-sm focus:ring-indigo-500"
                    />
                    {isExerciseStartInPast(formData.planned_windows?.[0]?.start) && (
                      <p className="text-xs text-red-500 mt-1">{PAST_EXERCISE_TIME_ERROR}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> End Time
                    </label>
                    <Input
                      type="datetime-local"
                      data-testid="exercise-end-time"
                      disabled={!!formData.planned_windows?.[0]?.end_open}
                      value={formData.planned_windows?.[0]?.end || ''}
                      onChange={(e) => {
                        const w = [...(formData.planned_windows || [{ start: '', end: '' }])];
                        w[0] = { ...(w[0] || {}), end: e.target.value };
                        updateFormData('planned_windows', w);
                      }}
                      className="h-9 text-sm focus:ring-indigo-500 disabled:bg-slate-100 disabled:text-slate-400"
                    />
                    {!formData.planned_windows?.[0]?.end_open
                      && formData.planned_windows?.[0]?.start
                      && formData.planned_windows?.[0]?.end
                      && new Date(formData.planned_windows[0].end) <= new Date(formData.planned_windows[0].start) && (
                      <p className="text-xs text-red-500 mt-1">End time must be after start time.</p>
                    )}
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="exercise-open-end"
                    checked={!!formData.planned_windows?.[0]?.end_open}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const w = [...(formData.planned_windows || [{ start: '', end: '' }])];
                      w[0] = { ...(w[0] || {}), end_open: checked, end: checked ? '' : (w[0]?.end || '') };
                      updateFormData('planned_windows', w);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-semibold text-slate-700">Open End (close on completion)</span>
                </label>
              </div>
            )}

            {/* Smart Match — moved below Exercise Classification (see Exercise section) */}

            {/* Exercise Classification — surfaced above Incident Type */}
            {formData.reportType === 'Exercise' && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Exercise Classification <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.exercise_classification}
                  onValueChange={(value) => updateFormData('exercise_classification', value)}
                >
                  <SelectTrigger data-testid="exercise-classification-select">
                    <SelectValue placeholder="Select exercise type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXERCISE_CLASSIFICATIONS.map((cls) => (
                      <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Incident Classification - Same layout as working NewIncident */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    Incident Type <span className="text-black-500">*</span>
                  </label>
                  <InlineTypeCreator
                    onTypeCreated={handleTypeCreated}
                  />
                </div>
                <Select
                  value={formData.type_id}
                  onValueChange={(value) => {
                    if (value) {
                      updateFormData('type_id', value);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select incident type" />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-72 overflow-y-auto">
                    <div className="sticky top-0 z-10 bg-white border-b p-2">
                      <Input
                        value={typeSearch}
                        onChange={(e) => setTypeSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder="Search incident type..."
                        className="h-8"
                      />
                    </div>
                    {filteredIncidentTypes.length > 0 ? filteredIncidentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center justify-between w-full">
                          <span className="flex-1">{type.incident_type}</span>
                          <SeverityChip
                            severity={type.alert_level || (type.incident_severity === 'HIGH' ? 'High' :
                              type.incident_severity === 'MEDIUM' ? 'Medium' :
                                type.incident_severity === 'LOW' ? 'Low' : 'Medium')}
                            size="sm"
                          />
                        </div>
                      </SelectItem>
                    )) : (
                      <SelectItem value="no-incident-type-results" disabled>
                        No result found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {inlineCreatedItems.type && formData.type_id === inlineCreatedItems.type.id && (
                  <CreatedChip type="type" />
                )}
                {!hasIncidentType && (
                  <p className="text-xs text-red-500 mt-1">This field is required.</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    Event
                  </label>
                  <InlineEventCreator
                    selectedTypeId={formData.type_id}
                    onEventCreated={handleEventCreated}
                    disabled={!formData.type_id}
                  />
                </div>
                <Select
                  value={formData.event_id}
                  onValueChange={(value) => {
                    if (value) {
                      updateFormData('event_id', value);
                    }
                  }}
                  disabled={!formData.type_id}
                >
                  <SelectTrigger className="focus:outline-none focus:ring-2 focus:ring-violet-500">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    sideOffset={5}
                    className="z-50 max-h-72 overflow-y-auto"
                  >
                    <div className="sticky top-0 z-10 bg-white border-b p-2">
                      <Input
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder="Search event..."
                        className="h-8"
                      />
                    </div>
                    <SelectItem value="none">No specific event</SelectItem>
                    {filteredEvents.length > 0 ? filteredEvents.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{event.name}</span>
                          {event.severity && (
                            <Badge className={getSeverityColor(event.severity)}>
                              {event.severity}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    )) : eventSearch.trim() ? (
                      <SelectItem value="no-event-results" disabled>
                        No result found
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
                {inlineCreatedItems.event && formData.event_id && formData.event_id.includes(inlineCreatedItems.event.name) && (
                  <CreatedChip type="event" />
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    Trigger
                  </label>
                  <InlineTriggerCreator
                    selectedTypeId={formData.type_id}
                    selectedEventName={formData.event_id && formData.event_id !== "none" ?
                      events.find(e => e.id === formData.event_id)?.name : null}
                    onTriggerCreated={handleTriggerCreated}
                    disabled={!formData.event_id || formData.event_id === "none"}
                  />
                </div>
                <Select
                  value={formData.trigger_id}
                  onValueChange={(value) => {
                    if (value) {
                      updateFormData('trigger_id', value);
                    }
                  }}
                  disabled={!formData.event_id || formData.event_id === "none"}
                >
                  <SelectTrigger className="focus:outline-none focus:ring-2 focus:ring-violet-500">
                    <SelectValue placeholder={
                      !formData.event_id || formData.event_id === "none"
                        ? "Select event first"
                        : "Select trigger"
                    } />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    sideOffset={5}
                    className="z-50 max-h-72 overflow-y-auto"
                  >
                    <div className="sticky top-0 z-10 bg-white border-b p-2">
                      <Input
                        value={triggerSearch}
                        onChange={(e) => setTriggerSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder="Search trigger..."
                        className="h-8"
                      />
                    </div>
                    <SelectItem value="none">No specific trigger</SelectItem>
                    {(() => {
                      // Show appropriate message if no triggers available
                      if (filteredTriggers.length === 0 && formData.event_id && formData.event_id !== "none" && !triggerSearch.trim()) {
                        return [(
                          <SelectItem key="no-triggers" value="no-triggers" disabled>
                            No triggers defined for this event
                          </SelectItem>
                        )];
                      }

                      if (filteredTriggers.length === 0 && triggerSearch.trim()) {
                        return [(
                          <SelectItem key="no-trigger-results" value="no-trigger-results" disabled>
                            No result found
                          </SelectItem>
                        )];
                      }

                      return filteredTriggers.map((trigger) => (
                        <SelectItem key={trigger.id} value={trigger.id}>
                          <div className="flex items-center justify-between w-full">
                            <span>{trigger.name}</span>
                            {trigger.severity && (
                              <Badge className={`ml-2 ${getSeverityColor(trigger.severity)}`}>
                                {trigger.severity}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                {inlineCreatedItems.trigger && formData.trigger_id && formData.trigger_id.includes(inlineCreatedItems.trigger.name) && (
                  <CreatedChip type="trigger" />
                )}
              </div>
            </div>

            {/* Dynamic Severity Display with Origin */}
            {classificationBundle && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Incident Severity
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <SeverityChip severity={autoResolvedSeverity} size="lg" />
                    <span className="text-sm text-gray-600">
                      (from {calculateAutoSeverity().from})
                    </span>
                  </div>
                  <span className="text-gray-400">|</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Override:</span>
                    <Select
                      value={formData.severity}
                      onValueChange={(value) => {
                        if (value) {
                          updateFormData('severity', value);
                          setIsUserOverride(value !== 'auto');
                        }
                      }}

                    >
                      <SelectTrigger className="w-32 focus:outline-none focus:ring-2 focus:ring-violet-500">
                        <SelectValue placeholder="Use Auto" />
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        sideOffset={5}
                        className="z-50"
                      >
                        <SelectItem value="auto">Use Auto</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    {isUserOverride && (
                      <span className="text-xs text-amber-600 font-medium">Overridden by user</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Exercise (Disaster Simulation) fields are rendered AFTER the Location section below */}


            {/* Location */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">
                  Incident Location <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center space-x-1 transition-colors"
                  onClick={() => setGuidedSearch({ active: true, selectedParentId: null, breadcrumb: [] })}
                >
                  <HelpCircle className="h-3 w-3" />
                  <span>Guided Location</span>
                </button>
              </div>
              <LocationSelector
                value={formData.location}
                onChange={(value) => {
                  if (formData.reportType === 'Exercise') return handleExerciseLocationChange(value);
                  return updateFormData('location', value);
                }}
                mode="single"
                allowWildcard={false}
                placeholder="Select incident location..."
                className="w-full"
              />
              {!hasLocation && (
                <p className="text-xs text-red-500 mt-1">This field is required.</p>
              )}

              <GuidedLocationPicker
                locations={locations}
                guidedSearch={guidedSearch}
                setGuidedSearch={setGuidedSearch}
                onApplyLocation={(selection) => {
                  if (selection?.location_id) {
                    if (formData.reportType === 'Exercise') {
                      handleExerciseLocationChange(selection.location_id);
                    } else {
                      updateFormData('location', selection.location_id);
                    }
                  }
                  setGuidedSearch({ active: false, selectedParentId: null, breadcrumb: [] });
                }}
                onNavigateToCreateLocation={handleCloseRequest}
              />

              {/* Dynamic Map Preview for Selected Location */}
              {hasLocation && !unknownLocationSelected && (
                <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-1 duration-300">
                  {(() => {
                    const selectedLocationId = Array.isArray(formData.location) ? formData.location[0] : formData.location;
                    const selectedLoc = locations.find(l => l.id === selectedLocationId);
                    const previewPoint = resolveZoneCoords(locationZoneIndex, selectedLocationId);
                    if (previewPoint) {
                      return (
                        <>
                          <div className="relative h-32 w-full">
                            <MyGoogleMap
                              containerStyle={{ width: '100%', height: '100%' }}
                              center={{ lat: previewPoint.lat, lng: previewPoint.lng }}
                              markerPins={[{ lat: previewPoint.lat, lng: previewPoint.lng }]}
                            />
                            <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-md text-[10px] font-bold text-slate-700 shadow-sm border border-slate-200">
                              {selectedLoc?.auto_label || previewPoint.name || 'Selected Zone'}
                            </div>
                          </div>
                          <div className="bg-slate-50 p-2 text-[10px] text-slate-500 flex items-center justify-between border-t border-slate-100">
                            <span className="flex items-center gap-1">
                              <MapIcon className="h-3 w-3 text-violet-500" />
                              Coordinates: {previewPoint.lat.toFixed(4)}, {previewPoint.lng.toFixed(4)}
                            </span>
                            <Badge variant="outline" className="text-[9px] bg-white">
                              {previewPoint.inheritedFrom
                                ? `Inherited from ${previewPoint.inheritedFrom.name}`
                                : (selectedLoc?.zone_type || 'Location')}
                            </Badge>
                          </div>
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  className={`text-xs flex items-center space-x-1 transition-all ${unknownLocationSelected ? 'text-amber-600 font-bold' : 'text-slate-500 hover:text-slate-700'}`}
                  onClick={() => {
                    if (unknownLocationSelected) {
                      updateFormData('location', '');
                    } else {
                      updateFormData('location', 'unknown');
                    }
                  }}
                >
                  <MapPin className={`h-3 w-3 ${unknownLocationSelected ? 'animate-bounce' : ''}`} />
                  <span>{unknownLocationSelected ? 'Location marked as Unknown' : 'Unknown Location'}</span>
                </button>
                {unknownLocationSelected && (
                  <button
                    className="text-[10px] text-slate-400 hover:text-slate-600 underline"
                    onClick={() => updateFormData('location', '')}
                  >
                    Clear
                  </button>
                )}
              </div>
              {unknownLocationSelected && (
                <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2 animate-in fade-in slide-in-from-top-1">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-amber-700 leading-relaxed">
                    <strong>Note:</strong> We'll use your description and attachments to pinpoint this location later. Please ensure they are as detailed as possible.
                  </p>
                </div>
              )}
            </div>

            {/* Involved Organizations and Departments — real incidents: shown right
                below the location, auto-filled from its linked departments */}
            {formData.reportType === 'Real Incident' && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-500" data-testid="real-incident-departments">
                {involvedDepartmentsSection}
              </div>
            )}

            {/* Exercise (Disaster Simulation) — scenario-driven Smart Match */}
            {formData.reportType === 'Exercise' && (
              <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-500">
                {involvedDepartmentsSection}

                {/* Smart Match — surfaced below Exercise Classification */}
                {(perfectMatchScenario ? (
                  <div className="p-4 rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-sm" data-testid="perfect-match-card">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-sm">
                        <Sparkles className="h-4 w-4" />
                      </span>
                      <div>
                        <h4 className="text-sm font-extrabold text-emerald-900 tracking-tight">Perfect Match Found</h4>
                        <p className="text-[11px] text-emerald-700/80">A ready-to-run scenario matches your selections.</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-emerald-100 p-3 mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-slate-900" data-testid="perfect-match-name">{perfectMatchScenario.name}</span>
                        {perfectMatchScenario.level && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">{perfectMatchScenario.level}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2 text-[11px] text-slate-500">
                        {perfectMatchScenario.incident_type && <span className="px-2 py-0.5 bg-slate-100 rounded-md">{perfectMatchScenario.incident_type}</span>}
                        {perfectMatchScenario.exercise_classification && <span className="px-2 py-0.5 bg-slate-100 rounded-md">{perfectMatchScenario.exercise_classification}</span>}
                        {(perfectMatchScenario.location_names || []).slice(0, 2).map((n, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-100 rounded-md flex items-center gap-1"><MapPin className="h-3 w-3" />{n}</span>
                        ))}
                      </div>
                    </div>
                    <Button
                      type="button"
                      data-testid="start-exercise-match-btn"
                      onClick={() => handlePlanFromScenario(perfectMatchScenario.id)}
                      disabled={launchingScenario}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    >
                      {launchingScenario ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
                      Start the Exercise
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50" data-testid="no-match-card">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-amber-900">No matching scenario yet</h4>
                        <p className="text-[12px] text-amber-700/90 mb-3">Exercises run from scenarios only. Build a scenario with your current selections to get started.</p>
                        <Button
                          type="button"
                          data-testid="build-scenario-btn"
                          onClick={buildScenarioFromSelection}
                          disabled={!formData.type_id || !hasValidLocation(formData.location)}
                          className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
                        >
                          <Plus className="h-4 w-4 mr-1.5" /> Build a Scenario
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Manual selection */}
                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-testid="plan-from-scenario">
                  <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2 mb-1">
                    <Library className="h-4 w-4" /> Or pick a scenario manually
                  </h4>
                  <p className="text-[12px] text-indigo-700/80 mb-3">Browse all active scenarios and start the exercise from any of them.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <Select value={selectedScenarioId} onValueChange={setSelectedScenarioId}>
                        <SelectTrigger data-testid="scenario-select-trigger" className="bg-white">
                          <SelectValue placeholder={scenarios.length ? 'Select a scenario…' : 'No active scenarios available'} />
                        </SelectTrigger>
                        <SelectContent>
                          {scenarios.map((s) => (
                            <SelectItem key={s.id} value={String(s.id)}>{s.name}{s.level ? ` · ${s.level}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      data-testid="plan-from-scenario-btn"
                      onClick={() => handlePlanFromScenario()}
                      disabled={!selectedScenarioId || launchingScenario}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                    >
                      {launchingScenario ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
                      Start the Exercise
                    </Button>
                  </div>
                </div>
              </div>
            )}


            {/* Reporter Information */}
            {formData.reportType === 'Real Incident' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Reporter Name
                  </label>
                  <Input
                    value={formData.reporter_name}
                    onChange={(e) => updateFormData('reporter_name', e.target.value)}
                    placeholder="Name of person reporting the incident..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Reporter Contact
                  </label>
                  <Input
                    value={formData.reporter_contact}
                    onChange={(e) => updateFormData('reporter_contact', e.target.value)}
                    placeholder="Phone, email, or other contact..."
                  />
                </div>
              </div>
            )}

            {formData.reportType !== 'Exercise' && (
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Brain className="h-4 w-4 text-violet-500 animate-pulse" />
                <label className="block text-sm font-semibold bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-transparent drop-shadow-sm">
                  Describe the Incident with DisasterMS AI
                </label>
                {
                  aiLoaderDescription ? (
                    <div className="flex items-center space-x-1.5 px-2 py-0.5 bg-violet-100 rounded-full animate-pulse">
                      <Loader2 className="w-3.5 h-3.5 text-violet-600 animate-spin" />
                      <span className="text-[10px] font-bold text-violet-700 uppercase tracking-tight">Generating...</span>
                    </div>
                  ) : (
                    <div
                      className="group flex items-center space-x-1.5 px-2 py-0.5 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-full cursor-pointer transition-all active:scale-95 shadow-sm"
                      onClick={generateAiDescription}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-violet-600 group-hover:rotate-12 transition-transform" />
                      <span className="text-[10px] font-bold text-violet-700 uppercase tracking-tight">Generate AI</span>
                    </div>
                  )
                }
              </div>
              {aiError && (
                <div className="mb-2 p-2 bg-red-50 border border-red-100 rounded-lg flex items-center justify-between animate-in fade-in slide-in-from-top-1">
                  <p className="text-[10px] text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {aiError}
                  </p>
                  <button
                    type="button"
                    onClick={generateAiDescription}
                    className="text-[10px] font-bold text-red-700 hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}
              <textarea
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                placeholder="Describe what happened, when it occurred, and any immediate actions taken..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-200 hover:border-violet-300"
                rows={4}
              />
              {/* <div className="flex items-start space-x-2 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-lg">💡</span>
                <p className="text-sm text-blue-700 leading-relaxed">
                  Using DisasterMS AI will automatically detect and classify the incident type, Event, Trigger, and locate the incident accurately.
                </p>
              </div> */}
            </div>
            )}

            {/* Attachments Section */}
            {formData.reportType !== 'Exercise' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Attachments
              </label>
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-violet-400 transition-colors">
                {formData.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {formData.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-600">📎</span>
                          <span className="text-gray-700">{attachment.name}</span>
                          {attachment.size && (
                            <span className="text-gray-400 text-xs">({(attachment.size / 1024).toFixed(1)} KB)</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 text-xs"
                          onClick={() => {
                            const newAttachments = formData.attachments.filter((_, i) => i !== index);
                            updateFormData('attachments', newAttachments);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <input
                      type="file"
                      id="incident-attachment-input-existing"
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                    <div className="flex items-center space-x-3 mt-2">
                      <button
                        type="button"
                        className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                        onClick={() => document.getElementById('incident-attachment-input-existing').click()}
                      >
                        + Add another attachment
                      </button>

                      <button
                        type="button"
                        className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 flex items-center"
                        onClick={analyzeImageWithAI}
                        disabled={aiAnalyzingImage}
                      >
                        {aiAnalyzingImage ? 'Analyzing...' : 'Analyze image'}
                      </button>

                      {aiAnalysisResults && (
                        <button
                          type="button"
                          className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 flex items-center"
                          onClick={applyAiSuggestions}
                        >
                          Apply AI suggestions
                        </button>
                      )}
                    </div>

                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">Add photos, documents, or other files</p>
                    <input
                      type="file"
                      id="incident-attachment-input"
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        type="button"
                        className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                        onClick={() => document.getElementById('incident-attachment-input').click()}
                      >
                        + Add attachment
                      </button>
                    </div>

                  </div>
                )}
              </div>
            </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              {formData.reportType !== 'Exercise' && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseRequest}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              )}

              {formData.reportType === 'Exercise' ? null : (
                <button
                  type="submit"
                  className="flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-[15px] font-bold text-white transition-all hover:brightness-110 hover:-translate-y-px disabled:opacity-60 disabled:hover:translate-y-0"
                  style={{ background: 'linear-gradient(135deg,#dc2626,#b91c1c)', boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 8px 24px rgba(220,38,38,0.35)' }}
                  disabled={
                    isSubmitting ||
                    !hasValidLocation(formData.location)
                  }
                >
                  <AlertTriangle className="h-4 w-4" />
                  {isSubmitting ? 'Reporting...' : 'Report'}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* AI Chat Side Panel */}
        <div className={`${isAiChatOpen ? 'block' : 'hidden'} w-full md:w-[400px] border-l border-slate-100 flex-shrink-0 animate-in slide-in-from-right duration-500 ease-out`}>
          <AIChatPanel
            isOpen={isAiChatOpen}
            onClose={() => setIsAiChatOpen(false)}
            attachments={formData.attachments}
            locationAssistantRequested={locationAssistantRequested}
            onLocationAssistantHandled={() => setLocationAssistantRequested(false)}
            locations={locations} // PASS LOCATIONS
            currentData={{
              type_id: formData.type_id,
              event_id: formData.event_id,
              trigger_id: formData.trigger_id,
              description: formData.description,
              reportType: formData.reportType,
              location: formData.location,
              classification_complete: !!(formData.type_id),
              has_location: hasValidLocation(formData.location) && !isUnknownLocation(formData.location)
            }}
            onApplySuggestion={(suggestion) => {
              applyAiSuggestions(suggestion);
            }}
            onChatUpdate={(state) => {
              setAiChatHistory(state.messages);
              setAiAppliedSuggestion(state.appliedSuggestion);
            }}
            onNavigateToCreateLocation={handleCloseRequest}
          />
        </div>
        <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>{formData.reportType === 'Exercise' ? 'Discard Drill Request?' : 'Discard Incident Report?'}</AlertDialogTitle>
              <AlertDialogDescription>
                {formData.reportType === 'Exercise'
                  ? 'You have unsaved information in this drill request. Are you sure you want to close it?'
                  : 'You have unsaved information in this incident report. Are you sure you want to close it?'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex sm:flex-row gap-2">
              <AlertDialogCancel>Continue Editing</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDiscardReport}
              >
                {formData.reportType === 'Exercise' ? 'Discard Drill' : 'Discard Report'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={showAiConfirm} onOpenChange={setShowAiConfirm}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader className="relative">
              <button
                type="button"
                onClick={() => setShowAiConfirm(false)}
                className="absolute right-0 top-0 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              <AlertDialogTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-violet-500" />
                AI Description Ready
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600">
                An incident description already exists. Would you like to overwrite it with the new AI version or append it to the end?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex sm:flex-row gap-2">
              <button
                type="button"
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                onClick={() => {
                  updateFormData('description', pendingAiText);
                  setShowAiConfirm(false);
                }}
              >
                Overwrite
              </button>
              <button
                type="button"
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-md transition-colors shadow-sm"
                onClick={() => {
                  updateFormData('description', formData.description + "\n\n" + pendingAiText);
                  setShowAiConfirm(false);
                }}
              >
                Append
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default NewIncidentModal;
